import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    TCI_ENV_CTRL_ACTION_TYPE,
    ITCIAppEnvControlProviderRequest,
    ITCIApplicationDeployRequest,
    TCITenantSelectorType,
    TCIApplicationDeploymentMode,
    TCI_APP_PROPERTY_TYPE,
    USER_ROLES, TCITenantAppType
} from "@tci/common";
import {SelectItem} from "primeng/primeng";
import { OnlyNumberDirective } from "../../../../../ng-shared/ng-directives/ng-only-number/ng-only-number.directive";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../../../ng-app-details-menu";
import { AbstractTCITenantComponent } from "../../../../../ng-tenant/tci-tenant-component";
import { ApplicationEnvCtrlAppProperty, ApplicationEnvCtrlAppConfiguration } from "../../base-app-detail-env.interface";
import { BaseSubmenuEngVarsService } from "./base-submenu-eng-vars.service";
import { NgToastrService } from "../../../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgToolsService } from "../../../../../ng-shared/ng-util/ng-tools.service";
import { Observable, Subscription } from "rxjs";
import * as lodash from "lodash";
import * as moment from "moment";


@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_ENV_ENG_VARS.toString(),
    templateUrl: "./base-submenu-eng-vars.component.html",
    styleUrls: ["./base-submenu-eng-vars.component.less"]
})
export class BaseSubmenuEngVarsComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy {
    @ViewChild("focusThis") inputFocus: ElementRef;
    @ViewChild(OnlyNumberDirective) onlyNumber: OnlyNumberDirective;
    initDataEngineVars: ApplicationEnvCtrlAppConfiguration;
    optionPropertyType: SelectItem[];
    isRORoleUser = false;
    booleanTypeList: SelectItem[];
    private newEmptyProperty: ApplicationEnvCtrlAppProperty;
    private subscriptions: Subscription[] = [];
    private isOnPremise: boolean;
    showRefresh: boolean;
    refreshEnabled: boolean;

    constructor(
        protected engVarsService: BaseSubmenuEngVarsService,
        protected translate: TranslateService,
        protected toolsSvc: NgToolsService,
        protected toastr: NgToastrService
    ) {
        super();
    }

    ngOnInit(): void {
        this.newEmptyProperty = {
            "name": "",
            "datatype": TCI_APP_PROPERTY_TYPE.string,
            "value": "",
            "description": "",
            "dateValue": new Date(),
            "isCustomized": true,
            "isNewEmptyCustomized": true
        };
        // RETEST Check If Var Button is Removed and also if Set to Default is removed.
        if (this.input.application.remoteManagement) {
            this.isOnPremise = this.input.application.remoteManagement;
        } else {
            this.isOnPremise = false;
        }
        this.canShowRefresh(false);
        this.isRefreshEnabled(false);
        // Initialize component information
        this.optionPropertyType = lodash
            .filter(TCI_APP_PROPERTY_TYPE, (item) => {
                return item !== TCI_APP_PROPERTY_TYPE.integer && item !== TCI_APP_PROPERTY_TYPE.long
                    && item !== TCI_APP_PROPERTY_TYPE.password;
            }).map((value: string) => {
                return {
                    label: value,
                    value: value
                };
            });

        this.initEngineProperties();
        // Request current state
        const stateRequest: ITCIApplicationDeployRequest = {
            mode: TCIApplicationDeploymentMode.MODE_APP_DETAILS,
            appId: this.input.application.id,
            application: this.input.application
        };
        this.booleanTypeList = [{
                label: "false",
                value: "false"
            }, {
                label: "true",
                value: "true"
            }];

        this.subscriptions.push(
            this.engVarsService
                .getEnvCtrlService()
                .getTenantDeployProvider(this.input.tenantAppType)
                .isBusy(stateRequest)
                .subscribe(busy => {
                    this.engVarsService.setIsDisableField(busy);
                })
        );
        this.subscriptions.push(this.engVarsService.getEnvCtrlService().getStatusSubject()
            .subscribe((res: TCI_ENV_CTRL_ACTION_TYPE) => {
                if (res === TCI_ENV_CTRL_ACTION_TYPE.REFRESH) {
                    this.initEngineProperties();
                }
            }));

        const thisObj = this;
        this.engVarsService.getEnvCtrlService().getUserAuthForEnv()
                .subscribe(userAuth => {
                    thisObj.isRORoleUser = (userAuth.role === USER_ROLES.RO);
                });
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    initEngineProperties() {
        this.engVarsService.getEnvCtrlService().initEngineProperties(this.generateReqInput())
            .subscribe(res => {
                this.initDataEngineVars = lodash.cloneDeep(res.data);
                this.engVarsService.setOriginalResData(lodash.cloneDeep(res.data));

                this.formatInitDataEngineVars();
                this.engVarsService.initHighLightRow(this.engVarsService.getOriginalNativeSysProperties().length);
            });
    }

    sort(properties) {
        return properties.sort((a, b) => {
            return Intl.Collator(undefined, {
                numeric: true,
                sensitivity: "base"
            }).compare(a.name, b.name);
        });
    }

    formatInitDataEngineVars() {
        this.initDataEngineVars.nativeSysPropertyOverrides.forEach(item => {
            item = this.engVarsService.formatDataForProperty(item);
            const index = this.getInitPropertyIndex(item.name);
            if (index > -1) {
                this.initDataEngineVars.nativeSysProperties[index].value = item.value;
            }
        });

        this.initDataEngineVars.nativeSysProperties.forEach(item => {
            item = this.engVarsService.formatDataForProperty(item);
        });

        this.initDataEngineVars.nativeSysProperties = this.sort(this.initDataEngineVars.nativeSysProperties);

        this.initDataEngineVars.userSysProperties.forEach(item => {
            item.isCustomized = true;
            item = this.engVarsService.formatDataForProperty(item);
        });

        this.initDataEngineVars.userSysProperties = this.sort(this.initDataEngineVars.userSysProperties);

        this.initDataEngineVars.nativeSysProperties = lodash.concat(
            this.initDataEngineVars.nativeSysProperties,
            this.initDataEngineVars.userSysProperties
        );
    }

    generateReqInput(): ITCIAppEnvControlProviderRequest {
        return {
            actionType: TCI_ENV_CTRL_ACTION_TYPE.ENGINE_PROPERTIES,
            appId: this.input.application.id,
            tenantType: this.input.application.type,
            isOnPremise: this.isOnPremise
        };
    }

    onValueChange(newValue: string, property: ApplicationEnvCtrlAppProperty) {
        newValue = String(newValue);
        property.value = newValue;
        if (!property.isCustomized) {
            this.onSysValueChange(newValue, property);
        }
        this.updateEngineVarData();
    }

    // for use OnlyNumber directive input field
    onInputNumberValue($event, property: ApplicationEnvCtrlAppProperty) {
        const inputValue = $event.target.value;
        const formatValue = this.onlyNumber.cleanValueOnInput(inputValue);
        if (formatValue === inputValue && formatValue === property.value) {
            // if they are same, user must have been typed correct number, then call onValueChange function
            this.onValueChange(formatValue, property);
        } else {
            // user must have been typed incorrect value, just set the correct newValue back to ngModule
            property.value = formatValue;
        }
    }

    onSysValueChange(newValue: string, property: ApplicationEnvCtrlAppProperty) {
        const index = this.getInitPropertyOverridesIndex(property.name);
        if (index === -1) {
            this.initDataEngineVars.nativeSysPropertyOverrides.push({
                name: property.name,
                value: newValue
            });
        } else {
            if (property.datatype === TCI_APP_PROPERTY_TYPE.password && newValue === "") {
                this.toastr.error("No values updated or ensure there is no empty values");
                lodash.remove(this.initDataEngineVars.nativeSysPropertyOverrides, item => item.name === property.name);
            } else {
                const originalPropertyValue = this.getOriginalPropertyValue(property.name);
                const originalPropertyOverridesValue = this.getOriginalPropertyOverridesValue(property.name);
                this.initDataEngineVars.nativeSysPropertyOverrides[index].value = newValue;
                if (originalPropertyOverridesValue === undefined && String(newValue) === String(originalPropertyValue)) {
                    lodash.remove(this.initDataEngineVars.nativeSysPropertyOverrides, item => item.name === property.name);
                }
            }
        }
    }

    setDefault(property: ApplicationEnvCtrlAppProperty) {
        if (!this.isDisableField() && !this.isRORoleUser) {
            const originalPropertyValue = this.getOriginalPropertyValue(property.name);
            const originalPropertyOverridesValue = this.getOriginalPropertyOverridesValue(property.name);
            if (originalPropertyOverridesValue === undefined) {
                lodash.remove(this.initDataEngineVars.nativeSysPropertyOverrides, item => item.name === property.name);
            } else {
                const overrideIndex = this.getInitPropertyOverridesIndex(property.name);
                this.initDataEngineVars.nativeSysPropertyOverrides[overrideIndex].value = originalPropertyValue;
            }
            const propertyIndex = this.getInitPropertyIndex(property.name);
            this.initDataEngineVars.nativeSysProperties[propertyIndex].value = originalPropertyValue;
            // reflect change in calendar element on set default
            if (property.datatype === TCI_APP_PROPERTY_TYPE.datetime) {
                this.initDataEngineVars.nativeSysProperties[propertyIndex].dateValue = moment(originalPropertyValue).toDate();
            }
            this.updateEngineVarData();
        }
    }

    updateEngineVarData() {
        const postData = lodash.cloneDeep(this.engVarsService.getOriginalResData());
        postData.nativeSysPropertyOverrides = lodash.cloneDeep(this.initDataEngineVars.nativeSysPropertyOverrides);

        const userSysProperties: ApplicationEnvCtrlAppProperty[] = [];
        this.initDataEngineVars.nativeSysProperties.forEach(item => {
            item.value = String(item.value);
            // reflect change in calendar element on value change
            if (item.datatype === TCI_APP_PROPERTY_TYPE.datetime) {
                item.dateValue = moment(item.value).toDate();
            }
            if (item.isCustomized) {
                userSysProperties.push(lodash.omit(item, ["dateValue", "isCustomized", "isNewEmptyCustomized"]));
            }
        });
        postData.userSysProperties = userSysProperties;

        this.engVarsService.getEnvCtrlService()
            .setTabData(TCI_ENV_CTRL_ACTION_TYPE.ENGINE_PROPERTIES, {
                "nativeSysPropertyOverrides": this.initDataEngineVars.nativeSysPropertyOverrides,
                "postConfigData": postData
            });
    }

    getOriginalPropertyValue(propertyName: string) {
        const property = lodash.find(this.engVarsService.getOriginalNativeSysProperties(), {name: propertyName});
        return property ? property.value : undefined;
    }

    getOriginalPropertyOverridesValue(propertyName: string) {
        const property = lodash.find(this.engVarsService.getOriginalNativeSysPropertyOverrides(), {name: propertyName});
        return property ? property.value : undefined;
    }

    getInitPropertyIndex(propertyName: string): number {
        return lodash.findIndex(this.initDataEngineVars.nativeSysProperties, {name: propertyName});
    }

    getInitPropertyOverridesIndex(propertyName: string): number {
        return lodash.findIndex(this.initDataEngineVars.nativeSysPropertyOverrides, {name: propertyName});
    }

    onSelectCalendar(newDate: Date, property: ApplicationEnvCtrlAppProperty) {
        const dateStr = this.engVarsService.getISODateFormat(newDate);
        if (property.isCustomized) {
            property.value = dateStr;
            this.updateEngineVarData();
        } else {
            this.onValueChange(dateStr, property);
        }
    }

    private updateDatePropValue(value, prop: ApplicationEnvCtrlAppProperty) {
        if (prop.isCustomized) {
            value = this.engVarsService.getISODateFormat(value);
            prop.value = value;
            this.updateEngineVarData();
        } else {
            this.onValueChange(value, prop);
        }
    }

    onDateTimeChange($event, property: ApplicationEnvCtrlAppProperty) {
        const newDateTime = $event.target.value;
        const momentObj = moment(newDateTime, "YYYY-MM-DDTHH:mm:ssZ", true);
        // Stage only valid date inputs.
        if (momentObj.isValid()) {
            const dateStr = this.engVarsService.getISODateFormat(momentObj.toDate());
            this.updateDatePropValue(dateStr, property);
        }
    }

    onDateTimeBlur($event, property: ApplicationEnvCtrlAppProperty) {
        const newDateTime = $event.target.value;
        const momentObj = moment(newDateTime, "YYYY-MM-DDTHH:mm:ssZ", true);
        // Stage only invalid date inputs.
        if (momentObj.isValid() === false) {
            if (newDateTime === "") {
                this.toastr.error("The variable datetime can not be empty.");
            } else {
                this.toastr.error("Incorrect time format, will revert to original value.");
            }
            // On invalid date input, revert back to original value to disable the "push updates" button.
            if (property.isCustomized) {
                const orgProperties = this.engVarsService.getOriginalUserSysProperties();

                const index = orgProperties
                    .findIndex(prop => (prop.name === property.name && prop.datatype === property.datatype));
                const originalVal = index > -1 ? orgProperties[index].value : new Date();
                this.updateDatePropValue(originalVal, property);
            } else {
                this.setDefault(property);
            }
            this.toggleDisplayCalendar();
        }
    }

    onNameChange(newName: string, property: ApplicationEnvCtrlAppProperty) {
        if (newName === "") {
            this.toastr.error("The variable name can not be empty.");
        }
        property.name = newName;
        this.updateEngineVarData();
    }

    onDescChange(newDesc: string, property: ApplicationEnvCtrlAppProperty) {
        property.description = newDesc;
        this.updateEngineVarData();
    }

    onDatatypeChange(newType: string, property: ApplicationEnvCtrlAppProperty) {
        switch (newType) {
            case TCI_APP_PROPERTY_TYPE.number:
                property.value = "0";
                break;
            case TCI_APP_PROPERTY_TYPE.datetime:
                property.dateValue = property.dateValue || new Date();
                property.value = this.engVarsService.getISODateFormat(property.dateValue);
                break;
            case TCI_APP_PROPERTY_TYPE.boolean:
                property.value = "true";
                break;
            default:
                property.value = "";
        }
        const index = lodash.findIndex(this.engVarsService.getOriginalUserSysProperties(), item => {
            return item.name === property.name && item.datatype === property.datatype;
        });
        let tmpValue;
        if (index > -1) {
            tmpValue = this.engVarsService.getOriginalUserSysProperties()[index].value;
        }
        if (!lodash.isUndefined(tmpValue)) {
            property.value = tmpValue;
        }
        this.updateEngineVarData();
    }

    isShowSetToDefaultButton(item: ApplicationEnvCtrlAppProperty): boolean {
        return String(item.value) !== String(this.getOriginalPropertyValue(item.name)) && item.datatype !== TCI_APP_PROPERTY_TYPE.password;
    }

    isDisableField(): boolean {
        return this.engVarsService.getIsDisableField();
    }

    toggleDisplayPassword() {
        this.engVarsService.toggleDisplayPassword();
    }

    isShowPassword(): boolean {
        return this.engVarsService.getIsShowPassword();
    }

    toggleDisplayCalendar() {
        this.engVarsService.toggleDisplayCalendar();
    }

    hideDatePicker() {
        this.engVarsService.hideDatePicker();
    }

    isShowCalendar(): boolean {
        return this.engVarsService.getIsShowCalendar();
    }

    // sets the currently selected field for highlight
    isRowHighLight(i: number): boolean {
        return this.engVarsService.isRowHighLight(i);
    }

    setHighLightRow(i: number) {
        return this.engVarsService.setHighLightRow(i);
    }

    isDisableAddVariableButton() {
        return this.isDisableField() || this.isNewEmptyPropertyExist() || lodash.isUndefined(this.initDataEngineVars);
    }
    isNewEmptyPropertyExist(): boolean {
        let bool = false;
        if (this.initDataEngineVars && this.initDataEngineVars.nativeSysProperties) {
            bool = lodash.some(this.initDataEngineVars.nativeSysProperties, property => {
                return property.name === this.newEmptyProperty.name && property.isCustomized === this.newEmptyProperty.isCustomized;
            });
        }
        return bool;
    }
    addUserVariable() {
        if (!this.isDisableField() && !this.isNewEmptyPropertyExist()) {
            this.initDataEngineVars.nativeSysProperties.push(lodash.clone(this.newEmptyProperty));
            this.updateEngineVarData();
            this.setHighLightRow(this.initDataEngineVars.nativeSysProperties.length - 1);
            this.toolsSvc.setTimeout(() => {
                // TODO: a bug: the input does not focus
                if (this.inputFocus.nativeElement.className.includes("is-new-empty")) {
                    this.inputFocus.nativeElement.focus();
                }
            });
        }
    }

    deleteUserVariable(i: number) {
        if (!this.isDisableField()) {
            lodash.pullAt(this.initDataEngineVars.nativeSysProperties, [i]);
            this.updateEngineVarData();
        }
    }

    isNodeApp() {
        return TCITenantAppType.node.equals(this.input.tenantAppType);
    }

    canShowRefresh(state: boolean): void {
        this.showRefresh = state;
    }

    isRefreshEnabled(state: boolean): void {
        this.refreshEnabled = state;
    }

    refreshAppProperties() {
        if (!(!this.refreshEnabled || this.isRORoleUser || this.isDisableAddVariableButton())) {
            this.initEngineProperties();
        }
    }
}
